import { Api } from '@monorepo/shared/apiClient';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { getQueryConfig } from '@monorepo/shared/utils/queryUtils';
import { queryCache, QueryConfig, useQuery } from 'react-query';

type KeyParams = {
  projectId: string;
  key: Api.SupportedUserSettingsKey;
};

export const createUserSettingsQueryKey = ({ projectId, key }: KeyParams) =>
  ['user-settings', projectId, key] as const;

function mergeSettings<T>(
  defaultValue: T & object,
  data: (T & object) | undefined,
): T {
  return data ? { ...defaultValue, ...data } : defaultValue;
}

type UseUserSettingsParams<K extends Api.SupportedUserSettingsKey> = {
  config?: QueryConfig<
    Api.UserSettingsValueForKey<K> | undefined,
    Api.ErrorResponse
  >;
  defaultValue: Api.UserSettingsValueForKey<K>;
  errorMessage?: string;
  key: K;
  projectId?: string;
};

export function useUserSettings<K extends Api.SupportedUserSettingsKey>({
  config: inputConfig,
  defaultValue,
  errorMessage = 'Unable to load settings',
  key: settingsKey,
  projectId,
}: UseUserSettingsParams<K>) {
  const { showUserFriendlyErrorToast } = useToast();

  const isEnabled = !!projectId;
  const config = {
    ...getQueryConfig<Api.UserSettingsValueForKey<K> | undefined>(
      inputConfig,
      isEnabled,
    ),
    useErrorBoundary: false,
    onError: (error: Api.ErrorResponse) => {
      showUserFriendlyErrorToast(error, errorMessage);
      inputConfig?.onError?.(error);
    },
  };
  const queryKey = isEnabled
    ? createUserSettingsQueryKey({ projectId, key: settingsKey })
    : undefined;

  const { data, ...queryInfo } = useQuery<
    Api.UserSettingsValueForKey<K> | undefined,
    Api.ErrorResponse
  >(
    queryKey,
    () =>
      projectId
        ? Api.fetchUserSettingsByKey({ projectId, key: settingsKey })
        : Promise.resolve(undefined),
    config,
  );

  return {
    settings: mergeSettings(defaultValue, data),
    ...queryInfo,
  };
}

export function getCache<K extends Api.SupportedUserSettingsKey>(
  keyParams: Api.FetchUserSettingsParams<K>,
): Api.UserSettingsValueForKey<K> | undefined {
  return queryCache.getQueryData<Api.UserSettingsValueForKey<K> | undefined>(
    createUserSettingsQueryKey(keyParams),
  );
}

export function setCache<K extends Api.SupportedUserSettingsKey>(
  keyParams: Api.FetchUserSettingsParams<K>,
  value: Api.UserSettingsValueForKey<K>,
): void {
  queryCache.setQueryData(createUserSettingsQueryKey(keyParams), value);
}

export async function invalidateCache(keyParams: KeyParams): Promise<void> {
  await queryCache.invalidateQueries(createUserSettingsQueryKey(keyParams));
}
